import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { INSIGHTS } from "@/data/site";
import PageSeo from "@/components/PageSeo";

const COVERS = {
  "stop-starting-with-the-camera":
    "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&w=1600&q=75",
  "employer-branding-is-not-a-film":
    "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1600&q=75",
  "the-quiet-revival-of-corporate-storytelling":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=75",
  "founders-on-camera":
    "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=75",
  "the-business-of-production":
    "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1600&q=75",
};

export default function BlogArticle() {
  const { slug } = useParams();
  const article = INSIGHTS.find((a) => a.slug === slug);
  const others = INSIGHTS.filter((a) => a.slug !== slug).slice(0, 3);

  useEffect(() => {
    if (!article) document.title = "Field Notes — RELATABLE";
  }, [article]);

  if (!article) {
    return (
      <main className="bg-ink text-bone min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <p className="eyebrow text-fog mb-6">Field Notes — 404</p>
          <h1 className="font-display text-5xl tracking-tighter font-medium mb-8">Article not found.</h1>
          <Link to="/" className="eyebrow text-bone link-line">← Back to RELATABLE</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-bone text-ink min-h-screen">
      <PageSeo
        title={`${article.title} — RELATABLE Field Notes`}
        description={article.excerpt}
        path={`/insights/${article.slug}`}
        image={COVERS[article.slug]}
        type="article"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Field Notes", path: "/#insights" },
          { name: article.title, path: `/insights/${article.slug}` },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `https://relatable.studio/insights/${article.slug}#article`,
          "headline": article.title,
          "description": article.excerpt,
          "image": COVERS[article.slug],
          "url": `https://relatable.studio/insights/${article.slug}`,
          "articleSection": article.category,
          "inLanguage": "en",
          "author": { "@type": "Organization", "name": "Relatable Studio", "url": "https://relatable.studio" },
          "publisher": { "@type": "Organization", "name": "Relatable Studio", "url": "https://relatable.studio" },
        }}
      />
      {/* Header */}
      <section className="pt-28 md:pt-32 px-6 md:px-12 lg:px-24">
        <Link to="/#insights" className="inline-flex items-center gap-2 eyebrow text-ink/60 hover:text-ink link-line">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Field Notes
        </Link>

        <div className="mt-10 max-w-4xl">
          <p className="eyebrow text-ink/60">{article.category} · {article.date} · {article.readTime}</p>
          <h1 className="mt-6 font-display text-4xl md:text-6xl lg:text-7xl tracking-tighter font-medium leading-[0.95]">
            {article.title}
          </h1>
          <p className="mt-8 font-display text-xl md:text-2xl text-ink/75 leading-snug max-w-2xl">
            {article.excerpt}
          </p>
        </div>
      </section>

      {/* Hero image */}
      <section className="mt-12 md:mt-16 px-6 md:px-12 lg:px-24">
        <div className="relative w-full overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
          <img
            src={COVERS[article.slug]}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Body */}
      <article className="mt-16 md:mt-20 px-6 md:px-12 lg:px-24 pb-20">
        <div className="max-w-3xl mx-auto space-y-7">
          {(article.body || []).map((para, i) => (
            <motion.p
              key={`${article.slug}-para-${i}`}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={`text-ink/80 leading-relaxed ${i === 0 ? "text-xl md:text-2xl font-display text-ink/90" : "text-base md:text-lg"}`}
            >
              {para}
            </motion.p>
          ))}
        </div>

        <div className="mt-20 max-w-3xl mx-auto border-t border-ink/15 pt-10">
          <p className="eyebrow text-ink/60 mb-4">Written by the studio</p>
          <p className="text-ink/70 leading-relaxed">
            RELATABLE is a Bangalore-based storytelling, production and brand experience company. We help brands, marketing teams, and founders solve communication problems — before we pick up a camera.
          </p>
        </div>
      </article>

      {/* Related */}
      <section className="bg-ink text-bone py-16 md:py-20 px-6 md:px-12 lg:px-24">
        <p className="eyebrow text-fog mb-8">◇ Continue Reading</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line">
          {others.map((p) => (
            <Link
              key={p.slug}
              to={`/insights/${p.slug}`}
              className="bg-ink p-8 group hover:bg-charcoal transition-colors"
            >
              <p className="eyebrow text-fog">{p.category} · {p.date}</p>
              <h3 className="mt-4 font-display text-xl md:text-2xl tracking-tight font-medium leading-[1.15] text-bone group-hover:text-relatable transition-colors">
                {p.title}
              </h3>
              <span className="mt-6 inline-flex items-center gap-2 eyebrow text-bone/80 link-line">
                Read piece <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
